// src/contexts/VenueContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { getVenueById, getVenuesByOwnerId } from '../services/venueService';
import { useUser } from './UserContext';
import { Venue } from '../types/Venue';

interface VenueContextType {
  venues: Venue[];
  activeVenue: Venue | null;
  isLoading: boolean;
  error: string | null;
  refreshVenues: () => Promise<void>;
  setActiveVenue: (venueId: string | null) => Promise<void>;
}

const VenueContext = createContext<VenueContextType | undefined>(undefined);

export const VenueProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useUser();
  const [venues, setVenues] = useState<Venue[]>([]);
  const [activeVenue, setActiveVenueState] = useState<Venue | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch venues owned by the current user
  const fetchVenues = async () => {
    if (!currentUser) {
      setVenues([]);
      setActiveVenueState(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const userVenues = await getVenuesByOwnerId(currentUser.uid);
      setVenues(userVenues);
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching venues:', err);
      setError('Failed to load venues');
      setIsLoading(false);
    }
  };

  // Set the active venue by ID
  const setActiveVenue = async (venueId: string | null) => {
    if (!venueId) {
      setActiveVenueState(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // First check if the venue is already in our local state
      let venue = venues.find(v => v.id === venueId) || null;
      
      // If not found locally, fetch it from the database
      if (!venue) {
        venue = await getVenueById(venueId);
      }
      
      setActiveVenueState(venue);
      setIsLoading(false);
    } catch (err) {
      console.error('Error setting active venue:', err);
      setError('Failed to load venue details');
      setIsLoading(false);
    }
  };

  // Refresh the venues list
  const refreshVenues = async () => {
    await fetchVenues();
  };

  // Initial fetch of venues when the user changes
  useEffect(() => {
    fetchVenues();
  }, [currentUser?.uid]);

  const contextValue: VenueContextType = {
    venues,
    activeVenue,
    isLoading,
    error,
    refreshVenues,
    setActiveVenue
  };

  return (
    <VenueContext.Provider value={contextValue}>
      {children}
    </VenueContext.Provider>
  );
};

export const useVenue = () => {
  const context = useContext(VenueContext);
  if (context === undefined) {
    throw new Error('useVenue must be used within a VenueProvider');
  }
  return context;
};
